import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { TSelectOption } from 'src/app/project/components/input/select/select.model';
import { CombineCustomFieldsService } from 'src/app/project/modules/custom-fields/combine-custom-fields/combine-custom-fields.service';
import { TCustomField } from 'src/app/project/modules/custom-fields/custom-fields.model';
import { GET_CUSTOM_FIELDS } from 'src/app/project/modules/custom-fields/custom-fields.store';
import { restoreGroupingAndSorting } from 'src/app/project/modules/site/site-options/site-options-column-dropdown/utils/restore-grouping-and-sorting';
import { SiteTableOverviewService } from 'src/app/project/modules/site/site-table-overview/site-table-overview.service';
import { TColumn } from 'src/app/project/modules/site/site-table/columns/column.model';
import { generateDefaultOverviewColumns } from 'src/app/project/modules/site/site-table/columns/columns';
import { TableColumnsService } from 'src/app/project/modules/site/site-table/columns/table-columns.service';
import { CustomTableService } from 'src/app/project/modules/site/site-table/custom-table/custom-table.service';
import { TWorkspace } from 'src/app/project/modules/workspace/workspace.model';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { EWorkspaces } from 'src/app/project/modules/workspace/workspaces.enum';
import { TAdvancedFilterSingle, TSearchFilter } from '../../../models/advanced-filter.model';
import {
  filterIdToColumn,
  getAdvancedFilterFields,
  searchNewValue,
} from './advanced-filter-field-select-utils';

@Component({
  selector: 'pp-advanced-filter-field-select',
  templateUrl: './advanced-filter-field-select.component.html',
  styleUrls: ['./advanced-filter-field-select.component.scss'],
  standalone: false,
})
export class AdvancedFilterFieldSelectComponent implements OnInit, OnChanges {
  @Input() ppFilter: TAdvancedFilterSingle;
  @Input() ppAdvancedFilter: TSearchFilter;
  @Output() ppFilterChange = new EventEmitter<TAdvancedFilterSingle>();
  @Input() ppWorkspaceId: string;

  tableColumns: TSelectOption[] = [];
  value: string;

  private column: TColumn;

  constructor(
    private workspaceService: WorkspaceService,
    private tableColumnsService: TableColumnsService,
    private customTableService: CustomTableService,
    private siteTableOverviewService: SiteTableOverviewService,
    private combineCustomFieldsService: CombineCustomFieldsService,
  ) {}

  ngOnInit(): void {
    this.getFields();
  }

  ngOnChanges(): void {
    this.column = filterIdToColumn(this.ppFilter.id);

    this.value = this.column ? this.column.name : null;
  }

  getFields(): void {
    const workspace = this.workspaceService.findWorkspace(this.ppWorkspaceId);

    if (workspace) {
      const columns = this.tableColumnsService.getColumns();
      this.tableColumns = getAdvancedFilterFields(columns, workspace);

      return;
    } else if (
      this.ppWorkspaceId === EWorkspaces.OVERVIEW ||
      this.ppWorkspaceId === EWorkspaces.TIMELINE
    ) {
      const columns = this.tableColumnsService.getColumns();
      this.tableColumns = getAdvancedFilterFields(columns, null);
    }

    this.setOverviewFields();
  }

  changeFieldValue(newValue: TSelectOption): void {
    const value = searchNewValue(newValue.value, newValue.customFields);

    this.ppFilter.id = value.id;
    this.ppFilter.type = value.type;
    this.value = newValue.value;
    this.ppFilter.customFields = newValue.customFields;
    this.ppFilterChange.emit(this.ppFilter);
  }

  private setOverviewFields(): void {
    const backupColumns: TColumn[] = [];
    const customFields = Object.values(GET_CUSTOM_FIELDS()).filter(
      (customField) => customField.display,
    );

    const defaultColumns = generateDefaultOverviewColumns(true);
    const resettedColumns = this.customTableService.resetColumnData();
    const filteredColumns = this.siteTableOverviewService.filterOverviewColumns(
      defaultColumns,
      resettedColumns,
    );

    const workspaces = Object.values(this.workspaceService.getWorkspaces());
    const { columns } = this.checkColumnsChanged(
      filteredColumns,
      customFields,
      this.ppWorkspaceId,
      workspaces,
    );

    const newColumns = this.tryRestoreOverviewGroupingAndSorting(columns, backupColumns);

    if (
      this.ppWorkspaceId === EWorkspaces.REMINDERS ||
      this.ppWorkspaceId === EWorkspaces.TIMELINE
    ) {
      this.tableColumnsService.setColumns(newColumns);
    }

    this.tableColumns = getAdvancedFilterFields(newColumns, null);
  }

  checkColumnsChanged(
    columns: TColumn[],
    customFields: TCustomField[],
    workspaceId: string,
    workspaces: TWorkspace[],
  ): { columns: TColumn[] } {
    const combinedCustomFields = this.combineCustomFieldsService.getCombinedCustomFields(
      workspaceId,
      workspaces,
    );

    customFields.forEach((customField) => {
      const mergedCustomField = combinedCustomFields.find((field) => {
        const fieldData = {
          workspaceId: customField.workspaceId,
          customFieldId: customField.id,
        };

        return field.combinedIds.some((id) => id.customFieldId === fieldData.customFieldId);
      });

      const newColumn: TColumn = {
        name: customField.label,
        index: columns.length,
        width: null,
        showTotal: customField.showTotal || typeof customField.showTotal === 'undefined',
        customFieldIds: [customField.id],
        unit: customField.unit,
        decimalPlaces: +customField.decimalPlaces,
        currency: customField.currencyCode,
        subValuesActive: !!customField.subValuesActive,
        workspaceId: customField.workspaceId,
      };

      const existingColumn = columns.find((column) =>
        mergedCustomField?.combinedIds.some((id) =>
          column.customFieldIds?.includes(id.customFieldId),
        ),
      );

      if (!existingColumn) {
        columns.push(newColumn);
      } else {
        existingColumn.customFieldIds.push(customField.id);
      }
    });

    return {
      columns,
    };
  }

  private tryRestoreOverviewGroupingAndSorting(
    columns: TColumn[],
    backupColumns: TColumn[],
  ): TColumn[] {
    const newColumns = restoreGroupingAndSorting(backupColumns, columns);

    return newColumns;
  }
}
